// components/product/ProductHeroBit.jsx
import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import RatingStars from '../ui/RatingStars';
import { Reveal } from '../animations/Reveal';
import {
    FaHeart,
    FaRegHeart,
    FaShareAlt,
    FaTruck,
    FaShieldAlt,
    FaLeaf,
    FaArrowLeft,
    FaArrowRight,
    FaExpand,
    FaChevronDown,
    FaCopy,
    FaWhatsapp,
    FaFacebook,
    FaTwitter,
    FaEnvelope,
    FaLinkedin,
    FaPinterest,
    FaTimes
} from 'react-icons/fa';
import '../../styles/globals.css';

const ProductHeroBit = ({
                            product,
                            onAddToCart,
                            isLoading: cartLoading,
                            selectedVariant,
                            onVariantSelect,
                            quantity,
                            onQuantityChange,
                            onBuyNow,
                            currentPrice,
                            originalPrice,
                            variantStockText,
                            variantStockColor
                        }) => {
    const containerRef = useRef(null);
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showToast, setShowToast] = useState({ visible: false, message: '', type: '' });

    // Helper function to check if a variant is in stock
    const isVariantInStock = (variant) => {
        if (!variant) return false;
        return variant.inStock || false;
    };

    // Get variant stock quantity
    const getVariantStock = (variant) => {
        if (!variant) return 0;
        return variant.stock || 0;
    };

    // Get the best available variant
    const getBestAvailableVariant = (product) => {
        if (!product || !product.variants) return null;

        // Try to find a variant that's in stock
        const inStockVariant = product.variants.find(variant =>
            isVariantInStock(variant)
        );

        // If no variant is in stock, return the default or first variant
        return inStockVariant ||
            product.stockInfo?.defaultVariant ||
            (product.variants.length > 0 ? product.variants[0] : null);
    };

    // Calculate prices with the SAME logic as ProductGridBit
    const calculatePricing = (product, selectedVariant = null) => {
        // Use selected variant or best available variant
        const variant = selectedVariant || getBestAvailableVariant(product);
        const variantPrice = variant?.price || 0;

        // Same logic as ProductGridBit formatProductData
        const price = variantPrice || product.price || product.discountPrice || 0;
        const originalPrice = product.originalPrice || product.price || 0;
        const discount = product.discount ||
            (originalPrice > price ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0);

        // Calculate save amount
        const saveAmount = originalPrice - price;

        return {
            currentPrice: price,
            originalPrice: originalPrice,
            discount: discount,
            saveAmount: saveAmount,
            variant: variant
        };
    };

    // Calculate pricing for display
    const { currentPrice: displayPrice, originalPrice: displayOriginalPrice, discount: displayDiscount, saveAmount: displaySaveAmount } =
        calculatePricing(product, selectedVariant);

    // Get variant stock display text
    const getVariantStockText = (variant) => {
        if (!variant) return '';

        if (!variant.inStock) {
            return 'Out of Stock';
        }

        if (variant.isLowStock && variant.stock > 0) {
            return `Low Stock (${variant.stock} left)`;
        }

        if (variant.stock > 0) {
            return `In Stock (${variant.stock} available)`;
        }

        return 'Out of Stock';
    };

    // Get variant stock color
    const getVariantStockColor = (variant) => {
        if (!variant || !variant.inStock) {
            return 'text-charcoal/60';
        }

        if (variant.isLowStock) {
            return 'text-amber-600';
        }

        return 'text-sage';
    };

    const handleAddToCartClick = async () => {
        setIsLoading(true);
        try {
            await onAddToCart();
        } catch (error) {
            console.error('Error adding to cart:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBuyNowClick = async () => {
        setIsLoading(true);
        try {
            await onAddToCart();
            await onBuyNow();
        } catch (error) {
            console.error('Error processing purchase:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const nextImage = () => {
        if (product.images && product.images.length > 0) {
            setActiveImageIndex((prev) => (prev + 1) % product.images.length);
        }
    };

    const prevImage = () => {
        if (product.images && product.images.length > 0) {
            setActiveImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
        }
    };

    // Share functionality
    const productSlug = product.slug || product.id || product._id;
    const productUrl = `${window.location.origin}/product/${productSlug}`;
    const shareText = `Check out "${product.name}" - $${displayPrice.toFixed(2)}\n${product.description || 'Premium skincare product'}`;
    const shareTitle = `Check out ${product.name} | KROMAIC`;

    // Show toast notification
    const showToastMessage = (message, type = 'success') => {
        setShowToast({ visible: true, message, type });
        setTimeout(() => {
            setShowToast({ visible: false, message: '', type: '' });
        }, 3000);
    };

    // Copy link to clipboard
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(productUrl);
            showToastMessage('Link copied to clipboard!', 'success');
            setShowShareModal(false);
        } catch (err) {
            console.error('Failed to copy:', err);
            showToastMessage('Failed to copy link', 'error');
        }
    };

    // Native Web Share API (mobile-first approach)
    const nativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: product.name,
                    text: shareText,
                    url: productUrl,
                });
                setShowShareModal(false);
            } catch (err) {
                console.log('Share cancelled:', err);
            }
        } else {
            copyToClipboard();
        }
    };

    // Share via WhatsApp
    const shareViaWhatsApp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + productUrl)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
        setShowShareModal(false);
    };

    // Share via Facebook
    const shareViaFacebook = () => {
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank', 'width=600,height=500,noopener,noreferrer');
        setShowShareModal(false);
    };

    // Share via Twitter
    const shareViaTwitter = () => {
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(productUrl)}`;
        window.open(url, '_blank', 'width=600,height=400,noopener,noreferrer');
        setShowShareModal(false);
    };

    // Share via Email
    const shareViaEmail = () => {
        const subject = encodeURIComponent(`Check out: ${product.name}`);
        const body = encodeURIComponent(`${shareText}\n\n${productUrl}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        setShowShareModal(false);
    };

    // Share via LinkedIn
    const shareViaLinkedIn = () => {
        const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(productUrl)}`;
        window.open(url, '_blank', 'width=600,height=500,noopener,noreferrer');
        setShowShareModal(false);
    };

    // Share via Pinterest
    const shareViaPinterest = () => {
        const imageUrl = product.images?.[0] || '';
        const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(productUrl)}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank', 'width=600,height=500,noopener,noreferrer');
        setShowShareModal(false);
    };

    // Share options data
    const shareOptions = [
        {
            id: 'native',
            name: 'Share',
            icon: FaShareAlt,
            color: 'text-sage',
            bgColor: 'bg-sage/10',
            onClick: nativeShare,
            show: navigator.share ? true : false
        },
        {
            id: 'copy',
            name: 'Copy Link',
            icon: FaCopy,
            color: 'text-charcoal',
            bgColor: 'bg-charcoal/10',
            onClick: copyToClipboard,
            show: true
        },
        {
            id: 'whatsapp',
            name: 'WhatsApp',
            icon: FaWhatsapp,
            color: 'text-green-600',
            bgColor: 'bg-green-50',
            onClick: shareViaWhatsApp,
            show: true
        },
        {
            id: 'facebook',
            name: 'Facebook',
            icon: FaFacebook,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            onClick: shareViaFacebook,
            show: true
        },
        {
            id: 'twitter',
            name: 'Twitter',
            icon: FaTwitter,
            color: 'text-blue-400',
            bgColor: 'bg-blue-50/50',
            onClick: shareViaTwitter,
            show: true
        },
        {
            id: 'email',
            name: 'Email',
            icon: FaEnvelope,
            color: 'text-red-500',
            bgColor: 'bg-red-50',
            onClick: shareViaEmail,
            show: true
        },
        {
            id: 'linkedin',
            name: 'LinkedIn',
            icon: FaLinkedin,
            color: 'text-blue-700',
            bgColor: 'bg-blue-50/80',
            onClick: shareViaLinkedIn,
            show: true
        },
        {
            id: 'pinterest',
            name: 'Pinterest',
            icon: FaPinterest,
            color: 'text-red-600',
            bgColor: 'bg-red-50/80',
            onClick: shareViaPinterest,
            show: true
        }
    ];

    // Check if selected variant is in stock
    const isSelectedVariantInStock = selectedVariant ? isVariantInStock(selectedVariant) : false;
    const isSelectedVariantLowStock = selectedVariant?.isLowStock || false;
    const selectedVariantStock = selectedVariant ? getVariantStock(selectedVariant) : 0;

    return (
        <section
            ref={containerRef}
            className="relative min-h-screen lg:min-h-[90vh] overflow-hidden pt-16 lg:pt-20"
        >
            {/* Toast Notification */}
            <AnimatePresence>
                {showToast.visible && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className={`fixed top-4 right-4 px-4 py-3 rounded-lg shadow-lg z-[100] ${
                            showToast.type === 'success' ? 'bg-sage text-white' : 'bg-red-500 text-white'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <span>{showToast.type === 'success' ? '✓' : '✗'}</span>
                            <span>{showToast.message}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="relative overflow-hidden">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-pearl via-ivory to-nude/30"/>

                <div className="container-luxury relative z-10 py-16 lg:py-24">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                        {/* Image Gallery */}
                        <div className="space-y-6">
                            {/* Main Image */}
                            <Reveal>
                                <div
                                    className="relative aspect-square rounded-3xl overflow-hidden bg-white shadow-lift">
                                    {product.images && product.images.length > 0 ? (
                                        <motion.img
                                            src={product.images[activeImageIndex]}
                                            alt={product.name}
                                            className="w-full h-full object-cover cursor-zoom-in"
                                            onClick={() => setIsZoomed(true)}
                                            animate={{scale: 1}}
                                            whileHover={{scale: 1.02}}
                                            transition={{duration: 0.4}}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-nude/20 flex items-center justify-center">
                                            <span className="text-charcoal/40">No image available</span>
                                        </div>
                                    )}

                                    {/* Image Navigation */}
                                    {product.images && product.images.length > 1 && (
                                        <div className="absolute top-4 right-4 flex gap-2">
                                            <motion.button
                                                className="w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-medium"
                                                onClick={prevImage}
                                                whileHover={{scale: 1.1}}
                                                whileTap={{scale: 0.9}}
                                            >
                                                <FaArrowLeft className="w-4 h-4 text-charcoal"/>
                                            </motion.button>
                                            <motion.button
                                                className="w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-medium"
                                                onClick={nextImage}
                                                whileHover={{scale: 1.1}}
                                                whileTap={{scale: 0.9}}
                                            >
                                                <FaArrowRight className="w-4 h-4 text-charcoal"/>
                                            </motion.button>
                                            <motion.button
                                                className="w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-medium"
                                                onClick={() => setIsZoomed(true)}
                                                whileHover={{scale: 1.1}}
                                                whileTap={{scale: 0.9}}
                                            >
                                                <FaExpand className="w-4 h-4 text-charcoal"/>
                                            </motion.button>
                                        </div>
                                    )}

                                    {/* Badges */}
                                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                                        {displayDiscount > 0 && (
                                            <motion.span
                                                initial={{scale: 0}}
                                                animate={{scale: 1}}
                                                className="px-3 py-1.5 bg-champagne text-charcoal text-sm font-medium rounded-full"
                                            >
                                                -{displayDiscount}%
                                            </motion.span>
                                        )}
                                        {!isSelectedVariantInStock && (
                                            <motion.span
                                                initial={{scale: 0}}
                                                animate={{scale: 1}}
                                                transition={{delay: 0.1}}
                                                className="px-3 py-1.5 bg-charcoal/80 text-white text-sm font-medium rounded-full"
                                            >
                                                Out of Stock
                                            </motion.span>
                                        )}
                                        {isSelectedVariantLowStock && isSelectedVariantInStock && (
                                            <motion.span
                                                initial={{scale: 0}}
                                                animate={{scale: 1}}
                                                transition={{delay: 0.1}}
                                                className="px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-full"
                                            >
                                                Low Stock
                                            </motion.span>
                                        )}
                                        <motion.span
                                            initial={{scale: 0}}
                                            animate={{scale: 1}}
                                            transition={{delay: 0.2}}
                                            className="px-3 py-1.5 bg-sage text-white text-sm font-medium rounded-full"
                                        >
                                            {product.isNew !== false ? 'New Launch' : 'Featured'}
                                        </motion.span>
                                    </div>

                                    {/* Image Counter */}
                                    {product.images && product.images.length > 1 && (
                                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                                            <div className="flex gap-2">
                                                {product.images.map((_, index) => (
                                                    <button
                                                        key={index}
                                                        onClick={() => setActiveImageIndex(index)}
                                                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                                            index === activeImageIndex ? 'bg-champagne w-4' : 'bg-nude'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Reveal>

                            {/* Thumbnail Gallery */}
                            {product.images && product.images.length > 1 && (
                                <Reveal delay={0.2}>
                                    <div className="grid grid-cols-4 gap-3">
                                        {product.images.map((image, index) => (
                                            <motion.button
                                                key={index}
                                                onClick={() => setActiveImageIndex(index)}
                                                className={`aspect-square rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                                                    index === activeImageIndex
                                                        ? 'border-champagne scale-105'
                                                        : 'border-transparent hover:border-nude'
                                                }`}
                                                whileHover={{scale: 1.05}}
                                                whileTap={{scale: 0.95}}
                                            >
                                                <img
                                                    src={image}
                                                    alt={`${product.name} view ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </motion.button>
                                        ))}
                                    </div>
                                </Reveal>
                            )}
                        </div>

                        {/* Product Info */}
                        <div className="space-y-8">
                            {/* Category & Badges */}
                            <Reveal>
                                <div className="flex items-center gap-4">
                                    <span className="text-sm tracking-widest uppercase text-sage font-medium">
                                        {product.category || 'Signature Collection'}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <RatingStars rating={product.rating || 0}/>
                                        <span className="text-sm text-charcoal/60">
                                            ({product.reviewCount || 0} reviews)
                                        </span>
                                    </div>
                                </div>
                            </Reveal>

                            {/* Product Name */}
                            <Reveal delay={0.1}>
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-light leading-tight">
                                    {product.name || 'Product Name'}
                                </h1>
                            </Reveal>

                            {/* Description */}
                            <Reveal delay={0.2}>
                                <p className="text-xl text-charcoal/70 leading-relaxed">
                                    {product.description || product.detailedDescription || 'Product description'}
                                </p>
                            </Reveal>

                            {/* Price and Stock Display - Using consistent pricing logic */}
                            <Reveal delay={0.3}>
                                <div className="flex items-baseline gap-4">
                                    <span className="text-3xl font-light text-charcoal">
                                        ${displayPrice.toFixed(2)}
                                    </span>
                                    {displayOriginalPrice > displayPrice && (
                                        <>
                                            <span className="text-lg text-charcoal/40 line-through">
                                                ${displayOriginalPrice.toFixed(2)}
                                            </span>
                                            <span
                                                className="px-3 py-1 bg-champagne/20 text-champagne text-sm rounded-full">
                                                Save ${displaySaveAmount.toFixed(2)}
                                            </span>
                                        </>
                                    )}
                                </div>

                                {/* Stock Status */}
                                {selectedVariant && (
                                    <div className="mt-2">
                                        <p className={`text-sm font-medium ${getVariantStockColor(selectedVariant)}`}>
                                            {getVariantStockText(selectedVariant)}
                                        </p>
                                    </div>
                                )}
                            </Reveal>

                            {/* Variant Selector */}
                            {product.variants && product.variants.length > 0 && (
                                <Reveal delay={0.4}>
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-medium text-charcoal/60">Select Size</h3>
                                        <div className="flex flex-wrap gap-3">
                                            {product.variants.map(variant => {
                                                const isSelected = selectedVariant?.id === variant.id;
                                                const isOutOfStock = !variant.inStock;
                                                const variantStockText = getVariantStockText(variant);
                                                const variantStockColor = getVariantStockColor(variant);

                                                return (
                                                    <motion.button
                                                        key={variant.id}
                                                        onClick={() => !isOutOfStock && onVariantSelect(variant)}
                                                        disabled={isOutOfStock}
                                                        whileHover={!isOutOfStock ? { scale: 1.05 } : {}}
                                                        whileTap={!isOutOfStock ? { scale: 0.95 } : {}}
                                                        className={`
                                                            px-6 py-4 rounded-xl border-2 transition-all duration-300 text-left
                                                            ${isSelected ? 'border-champagne bg-champagne/10' : 'border-nude/50'}
                                                            ${isOutOfStock
                                                            ? 'opacity-50 cursor-not-allowed bg-charcoal/5'
                                                            : 'hover:border-champagne cursor-pointer hover:shadow-md'
                                                        }
                                                            min-w-[140px]
                                                        `}
                                                    >
                                                        <div>
                                                            <div className="font-medium text-charcoal text-lg">{variant.name}</div>
                                                            <div className="text-sm text-charcoal/60 mt-1">
                                                                ${variant.price}
                                                            </div>
                                                            <div className={`text-xs mt-2 font-medium ${variantStockColor}`}>
                                                                {variantStockText}
                                                            </div>
                                                        </div>
                                                        {isSelected && (
                                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-champagne text-white rounded-full flex items-center justify-center text-sm">
                                                                ✓
                                                            </div>
                                                        )}
                                                    </motion.button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </Reveal>
                            )}

                            {/* Quantity Selector */}
                            <Reveal delay={0.5}>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-sm font-medium text-charcoal/60">Quantity</h3>
                                        {isSelectedVariantInStock && (
                                            <span className="text-xs text-charcoal/40">
                                                Max: {selectedVariantStock} units
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center border border-nude/50 rounded-xl">
                                        <button
                                            onClick={() => onQuantityChange(quantity - 1)}
                                            disabled={quantity <= 1 || !isSelectedVariantInStock}
                                            className="px-6 py-4 text-charcoal/60 hover:text-charcoal disabled:opacity-30"
                                        >
                                            −
                                        </button>
                                        <span className="px-6 py-4 text-charcoal font-medium text-lg">{quantity}</span>
                                        <button
                                            onClick={() => onQuantityChange(quantity + 1)}
                                            disabled={!isSelectedVariantInStock || quantity >= selectedVariantStock}
                                            className="px-6 py-4 text-charcoal/60 hover:text-charcoal disabled:opacity-30"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </Reveal>

                            {/* CTA Buttons */}
                            <Reveal delay={0.6}>
                                <div className="flex flex-col sm:flex-row gap-4 pt-8">
                                    {/* Gradient Primary Button */}
                                    <motion.div
                                        className="flex-1"
                                        whileHover={{scale: 1.02}}
                                        whileTap={{scale: 0.98}}
                                    >
                                        <button
                                            onClick={handleAddToCartClick}
                                            disabled={isLoading || cartLoading || !isSelectedVariantInStock}
                                            className={`
                                                w-full px-8 py-4 rounded-full
                                                bg-gradient-to-r from-champagne via-champagne/90 to-champagne/80
                                                text-charcoal font-medium text-lg
                                                hover:from-champagne/90 hover:via-champagne/80 hover:to-champagne/70
                                                active:scale-95
                                                transition-all duration-300
                                                shadow-lg hover:shadow-xl
                                                disabled:opacity-50 disabled:cursor-not-allowed
                                                relative overflow-hidden group
                                                ${!isSelectedVariantInStock ? 'bg-charcoal/10 hover:bg-charcoal/10' : ''}
                                            `}
                                        >
                                            <span className="relative flex items-center justify-center gap-3">
                                                {isLoading || cartLoading ? (
                                                    <>
                                                        <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>
                                                            {!isSelectedVariantInStock ? 'Out of Stock' : 'Add to Cart'}
                                                        </span>
                                                        {isSelectedVariantInStock && (
                                                            <span className="group-hover:translate-x-1 transition-transform">
                                                                →
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </span>
                                            {isSelectedVariantInStock && (
                                                <span
                                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>
                                            )}
                                        </button>
                                    </motion.div>

                                    {/* Outline Secondary Button */}
                                    <motion.div
                                        className="flex-1"
                                        whileHover={{scale: 1.02}}
                                        whileTap={{scale: 0.98}}
                                    >
                                        <button
                                            onClick={handleBuyNowClick}
                                            disabled={isLoading || cartLoading || !isSelectedVariantInStock}
                                            className={`
                                                w-full px-8 py-4 rounded-full
                                                bg-transparent border-2 border-champagne/40
                                                text-champagne font-medium text-lg
                                                hover:border-champagne hover:bg-champagne/5
                                                active:scale-95
                                                transition-all duration-300
                                                hover:shadow-lg
                                                disabled:opacity-50 disabled:cursor-not-allowed
                                                relative overflow-hidden group
                                                ${!isSelectedVariantInStock ? 'border-charcoal/20 text-charcoal/40' : ''}
                                            `}
                                        >
                                            <span className="relative flex items-center justify-center gap-3">
                                                {isLoading || cartLoading ? (
                                                    <>
                                                        <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>Buy Now</span>
                                                        {isSelectedVariantInStock && (
                                                            <span className="group-hover:scale-110 transition-transform">
                                                                ✓
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </span>
                                            {isSelectedVariantInStock && (
                                                <span
                                                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-20 bg-champagne blur-xl transition-opacity duration-300"/>
                                            )}
                                        </button>
                                    </motion.div>
                                </div>
                            </Reveal>

                            {/* Action Buttons */}
                            <Reveal delay={0.7}>
                                <div className="flex gap-4 pt-4">
                                    <motion.button
                                        onClick={() => setIsWishlisted(!isWishlisted)}
                                        className="flex items-center gap-2 text-sm text-charcoal/60 hover:text-champagne transition-colors"
                                        whileHover={{x: 5}}
                                    >
                                        {isWishlisted ? (
                                            <FaHeart className="w-4 h-4 text-champagne"/>
                                        ) : (
                                            <FaRegHeart className="w-4 h-4"/>
                                        )}
                                        Wishlist
                                    </motion.button>

                                    <motion.button
                                        onClick={() => setShowShareModal(true)}
                                        className="flex items-center gap-2 text-sm text-charcoal/60 hover:text-champagne transition-colors"
                                        whileHover={{x: 5}}
                                    >
                                        <FaShareAlt className="w-4 h-4"/>
                                        Share
                                    </motion.button>
                                </div>
                            </Reveal>

                            {/* Trust Indicators */}
                            <Reveal delay={0.8}>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-8 border-t border-nude/50">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                                            <FaTruck className="w-4 h-4 text-sage"/>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-charcoal">Free Shipping</div>
                                            <div className="text-xs text-charcoal/40">Over $100</div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center">
                                            <FaShieldAlt className="w-4 h-4 text-champagne"/>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-charcoal">Secure Payment</div>
                                            <div className="text-xs text-charcoal/40">100% Safe</div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 rounded-full bg-nude/30 flex items-center justify-center">
                                            <FaLeaf className="w-4 h-4 text-charcoal/60"/>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-charcoal">Cruelty Free</div>
                                            <div className="text-xs text-charcoal/40">Vegan Formula</div>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>

                            {/* Scroll Indicator */}
                            <Reveal delay={0.9}>
                                <motion.div
                                    className="flex items-center justify-center gap-2 pt-8 text-sm text-charcoal/40 cursor-pointer"
                                    animate={{y: [0, 5, 0]}}
                                    transition={{duration: 2, repeat: Infinity}}
                                    onClick={() => {
                                        document.getElementById('benefits')?.scrollIntoView({behavior: 'smooth'});
                                    }}
                                >
                                    <span>Discover Benefits</span>
                                    <FaChevronDown className="w-3 h-3"/>
                                </motion.div>
                            </Reveal>
                        </div>
                    </div>
                </div>

                {/* Zoom Modal */}
                <AnimatePresence>
                    {isZoomed && product.images && product.images.length > 0 && (
                        <motion.div
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            exit={{opacity: 0}}
                            onClick={() => setIsZoomed(false)}
                            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        >
                            <motion.div
                                initial={{scale: 0.9, opacity: 0}}
                                animate={{scale: 1, opacity: 1}}
                                exit={{scale: 0.9, opacity: 0}}
                                onClick={(e) => e.stopPropagation()}
                                className="relative max-w-4xl w-full"
                            >
                                <img
                                    src={product.images[activeImageIndex]}
                                    alt={product.name}
                                    className="w-full h-auto rounded-2xl"
                                />
                                <button
                                    onClick={() => setIsZoomed(false)}
                                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                                >
                                    ✕
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Share Modal */}
            <AnimatePresence>
                {showShareModal && (
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        onClick={() => setShowShareModal(false)}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{scale: 0.9, opacity: 0, y: 20}}
                            animate={{scale: 1, opacity: 1, y: 0}}
                            exit={{scale: 0.9, opacity: 0, y: 20}}
                            onClick={(e) => e.stopPropagation()}
                            className="relative max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-nude/30">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold text-charcoal">Share Product</h3>
                                        <p className="text-sm text-charcoal/60 mt-1">Share this product with friends</p>
                                    </div>
                                    <button
                                        onClick={() => setShowShareModal(false)}
                                        className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-nude/20 transition-colors"
                                    >
                                        <FaTimes className="w-4 h-4 text-charcoal/60" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6">
                                {/* Product Preview */}
                                <div className="flex items-start gap-4 p-4 bg-nude/10 rounded-xl mb-6">
                                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex-shrink-0">
                                        {product.images?.[0] ? (
                                            <img
                                                src={product.images[0]}
                                                alt={product.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-nude/20 flex items-center justify-center">
                                                <span className="text-charcoal/40 text-xs">No image</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-medium text-charcoal truncate">{product.name}</h4>
                                        <p className="text-sm text-charcoal/60 mt-1 truncate">
                                            {product.description || 'Premium skincare product'}
                                        </p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-base font-semibold text-champagne">
                                                ${displayPrice.toFixed(2)}
                                            </span>
                                            {displayOriginalPrice > displayPrice && (
                                                <span className="text-sm text-charcoal/40 line-through">
                                                    ${displayOriginalPrice.toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Share Options Grid */}
                                <div className="grid grid-cols-4 gap-3">
                                    {shareOptions
                                        .filter(option => option.show)
                                        .map((option) => {
                                            const Icon = option.icon;
                                            return (
                                                <motion.button
                                                    key={option.id}
                                                    onClick={option.onClick}
                                                    whileHover={{scale: 1.05}}
                                                    whileTap={{scale: 0.95}}
                                                    className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-nude/20 transition-colors"
                                                >
                                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${option.bgColor}`}>
                                                        <Icon className={`w-5 h-5 ${option.color}`} />
                                                    </div>
                                                    <span className="text-xs font-medium text-charcoal text-center">
                                                        {option.name}
                                                    </span>
                                                </motion.button>
                                            );
                                        })}
                                </div>

                                {/* Copy Link Section */}
                                <div className="mt-6 pt-6 border-t border-nude/30">
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                value={productUrl}
                                                readOnly
                                                className="w-full px-4 py-2 text-sm bg-nude/10 border border-nude/30 rounded-lg text-charcoal truncate"
                                            />
                                        </div>
                                        <motion.button
                                            onClick={copyToClipboard}
                                            whileHover={{scale: 1.05}}
                                            whileTap={{scale: 0.95}}
                                            className="px-4 py-2 bg-sage text-white text-sm font-medium rounded-lg hover:bg-sage/90 transition-colors"
                                        >
                                            Copy
                                        </motion.button>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 bg-nude/5 border-t border-nude/30">
                                <p className="text-xs text-charcoal/60 text-center">
                                    Share this product link via your preferred platform
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default ProductHeroBit;